import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createApiClient, type UpdateUserSettingsRequest } from "@floline/api-client";
import type { UserSettings } from "@floline/api-client";
import { API_URL } from "@/env";
import { storeDateFormat } from "@/lib/date-format";

export const userSettingsKeys = {
  all: ["user-settings"] as const,
};

const apiClient = createApiClient(API_URL);

async function fetchUserSettings(): Promise<UserSettings> {
  const response = await apiClient.getUserSettings();
  return response.data;
}

interface UseUserSettingsOptions {
  enabled?: boolean;
}

export function useUserSettings(options: UseUserSettingsOptions = {}) {
  const { enabled = true } = options;
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: userSettingsKeys.all,
    queryFn: fetchUserSettings,
    staleTime: 5 * 60 * 1000,
    enabled,
  });

  useEffect(() => {
    if (query.data) {
      storeDateFormat(query.data.dateFormat);
    }
  }, [query.data]);

  const mutation = useMutation({
    mutationFn: async (data: UpdateUserSettingsRequest) => {
      const response = await apiClient.updateUserSettings(data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(userSettingsKeys.all, data);
      storeDateFormat(data.dateFormat);
    },
  });

  return {
    settings: query.data,
    isLoading: query.isLoading,
    /** True once the query has completed at least one fetch cycle (success or error). */
    isFetched: query.isFetched,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    updateSettings: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    updateError: mutation.error,
  };
}
